import {Animation} from '@PosterWhiteboard/animation/animation.class';
import {PositionType} from '@PosterWhiteboard/classes/item-animation.class';
import {util} from '@postermywall/fabricjs-2';
import type {SlideItem} from '@PosterWhiteboard/items/slideshow-item/slideshow-item.types';
import {AnimationType} from '@PosterWhiteboard/items/item/item.types';

export class OutroAnimation extends Animation {
  public override timeForAnimation(time: number): boolean {
    if (!this.item.page.getDuration()) {
      return false;
    }
    const endTime = this.item.getPageEndTime() - this.getAnimationTimeForItem() + 0.15;
    return time > endTime - 0.1 && time <= endTime;
  }

  public override getAnimatingSlide(): SlideItem {
    if (this.item.isSlideshow()) {
      return this.item.slides.getLastSlide();
    }
    throw new Error('Invalid item');
  }

  public override restoreDefaultStateBeforeAnimating(): void {
    let item = this.item;
    if (item.isSlideshow()) {
      item = item.slides.getLastSlide();
    }
    if ((item.isText() || item.isTextSlide()) && item.clone) {
      item.clone.exitEditing();
      this.item.page.activeSelection.clearSelection();
    }
  }

  public override initAnimation(): void {
    this.onStartValues = this.getOnStartValues();
    this.endingAnimationValues = this.getAnimationValues();
    this.startingAnimationValues = this.defaultValues;
    this.animatingItem.set(this.onStartValues);
  }

  public override setInitialBlockPositionParams(): void {
    if (this.item.isText() || this.item.isTextSlide()) {
      this.setTextItemBlockOrigin();
    } else if (this.item.isMediaSlide()) {
      this.setMediaItemBlockOrigin();
    }

    const theta = util.degreesToRadians(this.clipPath.angle);
    const extraDisplacement = 3;

    switch (this.direction) {
      case PositionType.RIGHT:
        this.block.set({
          left: this.clipPath.left - (extraDisplacement + this.clipPath.getScaledWidth()) * Math.cos(theta),
          top: this.clipPath.top - (extraDisplacement + this.clipPath.getScaledWidth()) * Math.sin(theta),
        });
        break;
      case PositionType.LEFT:
        this.block.set({
          left: this.clipPath.left + (extraDisplacement + this.clipPath.getScaledWidth()) * Math.cos(theta),
          top: this.clipPath.top + (extraDisplacement + this.clipPath.getScaledWidth()) * Math.sin(theta),
        });
        break;
      case PositionType.TOP:
        this.block.set({
          left: this.clipPath.left - (extraDisplacement + this.clipPath.getScaledHeight()) * Math.sin(theta),
          top: this.clipPath.top + (extraDisplacement + this.clipPath.getScaledHeight()) * Math.cos(theta),
        });
        break;
      case PositionType.BOTTOM:
        this.block.set({
          left: this.clipPath.left + (extraDisplacement + this.clipPath.getScaledHeight()) * Math.sin(theta),
          top: this.clipPath.top - (extraDisplacement + this.clipPath.getScaledHeight()) * Math.cos(theta),
        });
        break;
    }
  }

  public override getAnimationBlockCoordinates(): Record<string, number> {
    const theta = util.degreesToRadians(this.clipPath.angle);
    const extraDisplacement = 3;

    switch (this.direction) {
      case PositionType.RIGHT:
        return {
          left: this.clipPath.left + (extraDisplacement + this.clipPath.getScaledWidth()) * Math.cos(theta),
          top: this.clipPath.top + (extraDisplacement + this.clipPath.getScaledWidth()) * Math.sin(theta),
        };
      case PositionType.LEFT:
        return {
          left: this.clipPath.left - (extraDisplacement + this.clipPath.getScaledWidth()) * Math.cos(theta),
          top: this.clipPath.top - (extraDisplacement + this.clipPath.getScaledWidth()) * Math.sin(theta),
        };
      case PositionType.TOP:
        return {
          left: this.clipPath.left + (extraDisplacement + this.clipPath.getScaledHeight()) * Math.sin(theta),
          top: this.clipPath.top - (extraDisplacement + this.clipPath.getScaledHeight()) * Math.cos(theta),
        };
      case PositionType.BOTTOM:
      default:
        return {
          left: this.clipPath.left - (extraDisplacement + this.clipPath.getScaledHeight()) * Math.sin(theta),
          top: this.clipPath.top + (extraDisplacement + this.clipPath.getScaledHeight()) * Math.cos(theta),
        };
    }
  }

  public override getAnimationBounceCoordinates(): Record<string, number> {
    const bound = this.animatingItem.getBoundingRect();
    switch (this.direction) {
      case PositionType.RIGHT:
        return {
          opacity: 0,
          left: this.animatingItem.left + (bound.left + bound.width) + 10,
        };
      case PositionType.LEFT:
        return {
          opacity: 0,
          left: this.animatingItem.left - (bound.left + bound.width) - 10,
        };
      case PositionType.BOTTOM:
        return {
          opacity: 0,
          top: this.animatingItem.top + (bound.top + bound.height) + 10,
        };
      case PositionType.TOP:
      default:
        return {
          opacity: 0,
          top: this.animatingItem.top - (bound.top + bound.height) - 10,
        };
    }
  }

  public override getAnimationPanValues(): Record<string, number> {
    switch (this.direction) {
      case PositionType.RIGHT:
        return {
          opacity: 0,
          left: this.animatingItem.left + 0.2 * this.item.page.poster.width,
        };
      case PositionType.LEFT:
        return {
          opacity: 0,
          left: this.animatingItem.left - 0.2 * this.item.page.poster.width,
        };
      case PositionType.TOP:
        return {
          opacity: 0,
          top: this.animatingItem.top - 70,
        };
      case PositionType.BOTTOM:
      default:
        return {
          opacity: 0,
          top: this.animatingItem.top + 70,
        };
    }
  }

  public override getAnimationSlideValues(): Record<string, number> {
    const bound = this.animatingItem.getBoundingRect();
    switch (this.direction) {
      case PositionType.RIGHT:
        return {
          left: this.item.page.fabricCanvas.width / this.item.page.poster.scaling.scale - bound.left + this.animatingItem.left,
        };
      case PositionType.LEFT:
        return {
          left: this.animatingItem.left - (bound.left + bound.width),
        };
      case PositionType.BOTTOM:
        return {
          top: this.item.page.fabricCanvas.height / this.item.page.poster.scaling.scale - bound.top + this.animatingItem.top,
        };
      case PositionType.TOP:
      default:
        return {
          top: this.animatingItem.top - (bound.top + bound.height),
        };
    }
  }

  public override getAnimationWipeCoordinates(): Record<string, number> {
    const theta = util.degreesToRadians(this.animatingItem.angle);
    switch (this.direction) {
      case PositionType.BOTTOM:
        return {
          left: this.animatingItem.left - this.animatingItem.getScaledHeight() * Math.sin(theta),
          top: this.animatingItem.top + this.animatingItem.getScaledHeight() * Math.cos(theta),
        };
      case PositionType.TOP:
        return {
          left: this.animatingItem.left + this.animatingItem.getScaledHeight() * Math.sin(theta),
          top: this.animatingItem.top - this.animatingItem.getScaledHeight() * Math.cos(theta),
        };
      case PositionType.LEFT:
        return {
          left: this.animatingItem.left - this.animatingItem.getScaledWidth() * Math.cos(theta),
          top: this.animatingItem.top - this.animatingItem.getScaledWidth() * Math.sin(theta),
        };
      case PositionType.RIGHT:
      default:
        return {
          left: this.animatingItem.left + this.animatingItem.getScaledWidth() * Math.cos(theta),
          top: this.animatingItem.top + this.animatingItem.getScaledWidth() * Math.sin(theta),
        };
    }
  }

  public override getAnimationZoomScaleValues(): Record<string, number> {
    switch (this.direction) {
      case PositionType.RIGHT:
        return {
          opacity: 0,
          scaleX: this.animatingItem.scaleX / (this.type === AnimationType.SCALE ? 2 : 1.5),
          scaleY: this.animatingItem.scaleY / (this.type === AnimationType.SCALE ? 2 : 1.5),
        };
      case PositionType.LEFT:
      default:
        return {
          opacity: 0,
          scaleX: this.animatingItem.scaleX * (this.type === AnimationType.SCALE ? 2 : 1.5),
          scaleY: this.animatingItem.scaleY * (this.type === AnimationType.SCALE ? 2 : 1.5),
        };
    }
  }

  public override getAnimationPopValues(): Record<string, number> {
    return {
      opacity: 0,
      scaleX: this.animatingItem.scaleX * 1.5,
      scaleY: this.animatingItem.scaleY * 1.5,
    };
  }

  public override getAnimationPeekValues(): Record<string, number> {
    const theta = util.degreesToRadians(this.animatingItem.angle);
    switch (this.direction) {
      case PositionType.TOP:
        return {
          left: this.animatingItem.left + this.animatingItem.getScaledHeight() * Math.sin(theta),
          top: this.animatingItem.top - this.animatingItem.getScaledHeight() * Math.cos(theta),
        };
      case PositionType.BOTTOM:
        return {
          left: this.animatingItem.left - this.animatingItem.getScaledHeight() * Math.sin(theta),
          top: this.animatingItem.top + this.animatingItem.getScaledHeight() * Math.cos(theta),
        };
      case PositionType.RIGHT:
        return {
          left: this.animatingItem.left + this.animatingItem.getScaledWidth() * Math.cos(theta),
          top: this.animatingItem.top + this.animatingItem.getScaledWidth() * Math.sin(theta),
        };
      case PositionType.LEFT:
      default:
        return {
          left: this.animatingItem.left - this.animatingItem.getScaledWidth() * Math.cos(theta),
          top: this.animatingItem.top - this.animatingItem.getScaledWidth() * Math.sin(theta),
        };
    }
  }

  public override getEaseFunctionForPopAndJello(): (timeElapsed: number, startValue: number, byValue: number, duration: number) => number {
    return util.ease.easeInElastic;
  }

  public override getAnimationTimeForEaseFunction(time: number): number {
    return time - (this.item.getPageEndTime() - this.getAnimationTimeForItem());
  }

  public override getAnimationTimeForFinalFrame(): number {
    return this.item.getPageEndTime();
  }

  public override isFrameTimeForAnimation(time: number): boolean {
    const outroAnimationStartTime = this.item.getPageEndTime() - this.getAnimationTimeForItem();
    return time >= outroAnimationStartTime && time < this.item.getPageEndTime();
  }

  public override blockAnimationOnChange(): void {
    this.animatingItem.setCoords();

    if (this.shouldBlockBeMadeVisible() && this.getAnimatingItem().fabricObject.opacity !== 0) {
      this.getAnimatingItem().fabricObject.set('opacity', 0);
    }
  }
}
